import { useState } from "react";
import { useNavigate } from "react-router-dom";
import MainLayout from "../layout/MainLayout";
import { Button1, EmptyScreen, ToggleButton } from "../Components";
import { PendingTag, SuccessTag, FailedTag } from "../Components/tag/Tag";
import { useTransfers } from "../data/live";
import { transfersApi } from "../api/endpoints";

const formatAmount = (value, currency) => {
  const formatted = value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${formatted} ${currency}`;
};

const PendingRow = ({ t, onCancel, onView }) => (
  <tr>
    <td className="align-middle p-3 ps-4">
      <div className="d-flex flex-column">
        <span className="fw-medium" style={{ color: "#161616" }}>{t.type}</span>
        <span className="fs12px" style={{ color: "#7D7D7D" }}>
          to <span style={{ color: "#1BBB6B" }}>{t.counterparty}</span>
        </span>
      </div>
    </td>
    <td className="align-middle p-3">
      <span className="fs14px" style={{ color: "#161616" }}>
        {t.date}
      </span>
    </td>
    <td className="align-middle p-3 text-end">
      <span className="fw-semibold" style={{ color: "#161616" }}>
        -{formatAmount(t.amount, t.currency)}
      </span>
    </td>
    <td className="align-middle p-3">
      {t.status === "FAILED" ? (
        <FailedTag tagName="FAILED" className="rounded-2" />
      ) : t.status === "SUCCEED" ? (
        <SuccessTag tagName="SUCCEED" className="rounded-2" />
      ) : (
        <PendingTag tagName="PENDING" className="rounded-2" />
      )}
    </td>
    <td className="align-middle p-3 pe-4">
      <div className="d-flex gap-2 justify-content-end">
        <Button1
          buttonName="View"
          className="border"
          onClick={() => onView(t)}
        />
        <Button1
          buttonName="Cancel"
          className="btn-outline-danger"
          onClick={() => onCancel(t)}
        />
      </div>
    </td>
  </tr>
);

const ScheduledRow = ({ s, onEdit, onCancel }) => (
  <tr>
    <td className="align-middle p-3 ps-4">
      <div className="d-flex flex-column">
        <span className="fw-medium" style={{ color: "#161616" }}>{s.type}</span>
        <span className="fs12px" style={{ color: "#7D7D7D" }}>
          to <span style={{ color: "#1BBB6B" }}>{s.counterparty}</span>
        </span>
      </div>
    </td>
    <td className="align-middle p-3">
      <div className="d-flex flex-column">
        <span className="fs14px fw-medium">{s.nextRun}</span>
        <span className="fs12px" style={{ color: "#7D7D7D" }}>
          {s.repeat}
        </span>
      </div>
    </td>
    <td className="align-middle p-3 text-end">
      <div className="d-flex flex-column align-items-end">
        <span className="fw-semibold" style={{ color: "#161616" }}>
          -{formatAmount(s.amount, s.currency)}
        </span>
        {s.sourceAmount && (
          <span className="fs12px" style={{ color: "#7D7D7D" }}>
            from {formatAmount(s.sourceAmount, s.sourceCurrency)}
          </span>
        )}
      </div>
    </td>
    <td className="align-middle p-3">
      <span className="fs14px" style={{ color: "#7D7D7D" }}>
        Ends {s.endDate}
      </span>
    </td>
    <td className="align-middle p-3 pe-4">
      <div className="d-flex gap-2 justify-content-end">
        <Button1
          buttonName="Edit"
          className="border"
          onClick={() => onEdit(s)}
        />
        <Button1
          buttonName="Cancel"
          className="btn-outline-danger"
          onClick={() => onCancel(s)}
        />
      </div>
    </td>
  </tr>
);

const PendingTransactions = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("Pending");

  const { transfers: livePending, refresh } = useTransfers({
    status: "PENDING",
  });
  const [scheduled] = useState([]);

  // Map live transfer to the row shape
  const mapTransferRow = (t) => ({
    id: t.id,
    type: t.transferType,
    counterparty: t.beneficiary?.fullName || "-",
    amount: Number(t.sourceAmount),
    currency: t.sourceCurrency,
    status: t.status,
    date: new Date(t.createdAt).toLocaleString("en-AU", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }),
  });

  const pending = livePending.map(mapTransferRow);

  const cancelPending = async (t) => {
    if (!window.confirm(`Cancel pending ${t.type} to ${t.counterparty}?`))
      return;
    try {
      await transfersApi.cancel(t.id);
      await refresh();
    } catch (err) {
      alert(`Cancel failed: ${err.response?.data?.error || err.message}`);
    }
  };

  const viewPending = (t) => {
    navigate("/my-transaction", { state: { focusTxId: t.id } });
  };

  const editScheduled = (s) => {
    alert("Scheduled transfer editing is not yet implemented.");
  };

  const cancelScheduled = (s) => {
    alert("Scheduled transfer cancellation is not yet implemented via API.");
  };

  const isPending = activeTab === "Pending";
  const current = isPending ? pending : scheduled;
  const isEmpty = current.length === 0;

  return (
    <MainLayout
      NavName="Pending Transaction"
      expand={false}
      pageClassName="dashboard-page pending-transactions-page"
      contentClassName="dashboard-content"
    >
      <div className="pending-transactions-shell">
        <div className="pending-transactions-overview">
          <div>
            <span className="dashboard-eyebrow">Transfer queue</span>
            <h2>Pending Transactions</h2>
            <p>Review transfers waiting to process and manage scheduled payments in one place.</p>
          </div>
          <div className="d-flex gap-2 flex-wrap justify-content-md-end">
            <Button1
              buttonName="New Transfer"
              className="btn-dark"
              onClick={() => navigate("/new-transfer")}
            />
            <Button1
              buttonName="Schedule Transfer"
              className="btn-outline-success"
              onClick={() => navigate("/schedule-transfer")}
            />
          </div>
        </div>

        <div className="pending-transactions-card mt-4">
        <div className="pending-transactions-toolbar d-flex justify-content-between align-items-center flex-wrap gap-2">
          <ToggleButton
            options={["Pending", "Scheduled"]}
            defaultValue="Pending"
            onChange={(v) => setActiveTab(v)}
          />
          <span className="pending-transactions-count">
            {current.length} {isPending ? "pending" : "scheduled"}
          </span>
        </div>

        {isEmpty ? (
          <div className="pending-empty-state">
            <EmptyScreen
              heading={
                isPending
                  ? "No pending transactions"
                  : "No scheduled transfers"
              }
              message={
                isPending
                  ? "You don't have any transactions awaiting processing. In-flight transfers will appear here."
                  : "You haven't set up any recurring or future-dated transfers. Schedule one to see it here."
              }
              messageWidth="420px"
              headingWidth="360px"
            />
          </div>
        ) : (
          <div className="pending-table-wrap overflow-hidden">
            <table className="w-100 mb-0">
              <thead>
                <tr>
                  <th className="fs12px fw-medium p-3 ps-4" style={{ color: "#ffffff" }}>
                    Activity
                  </th>
                  <th className="fs12px fw-medium p-3" style={{ color: "#ffffff" }}>
                    {isPending ? "Date and Time" : "Next run"}
                  </th>
                  <th className="fs12px fw-medium p-3 text-end" style={{ color: "#ffffff" }}>
                    Amount
                  </th>
                  <th className="fs12px fw-medium p-3" style={{ color: "#ffffff" }}>
                    {isPending ? "Status" : "Ends on"}
                  </th>
                  <th className="fs12px fw-medium p-3 pe-4 text-end" style={{ color: "#ffffff" }}>
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {isPending
                  ? pending.map((t) => (
                      <PendingRow
                        key={t.id}
                        t={t}
                        onCancel={cancelPending}
                        onView={viewPending}
                      />
                    ))
                  : scheduled.map((s) => (
                      <ScheduledRow
                        key={s.id}
                        s={s}
                        onEdit={editScheduled}
                        onCancel={cancelScheduled}
                      />
                    ))}
              </tbody>
            </table>
          </div>
        )}
        </div>
      </div>
    </MainLayout>
  );
};

export default PendingTransactions;

